import React, { useEffect, useState } from 'react';
import { Loader2, Search, X } from 'lucide-react';
import financialInstitutionApiService, {
  type SupportProgramOption,
} from '../../services/financialInstitutionApi';
import { DEFAULT_SUPPORT_PROGRAM, isDefaultSupportProgram } from '../../config/defaultSupportProgram';

interface SupportProgramPickerProps {
  institutionId: string;
  name: string;
  province: string;
  disabled: boolean;
  /** Programme listé : id et nom ; saisie libre : id vide et nom tapé. */
  onChange: (next: { supportProgramInstitutionId: string; supportProgramName: string }) => void;
}

/**
 * Programme d'appui d'une AVEC, facultatif. Sans choix, l'AVEC est suivie par
 * la structure d'appui par défaut (« Réseau ikiotahub »), affichée comme telle.
 * Un autre programme se cherche par nom ou par province parmi les programmes
 * validés ; la saisie libre ne sert que s'il n'est pas listé.
 */
export const SupportProgramPicker: React.FC<SupportProgramPickerProps> = ({
  institutionId,
  name,
  province,
  disabled,
  onChange,
}) => {
  const [search, setSearch] = useState('');
  const [options, setOptions] = useState<SupportProgramOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [manual, setManual] = useState(!institutionId && !!name);
  // Recherche ouverte (« Choisir un autre programme ») ; sinon la structure par défaut s'affiche.
  const [choosing, setChoosing] = useState(false);
  // Logo du programme choisi : gardé au clic, sinon relu dans la liste.
  const [logo, setLogo] = useState<{ id: string; url: string | null } | null>(null);

  const usesDefault = isDefaultSupportProgram(institutionId) || (!institutionId && !name.trim());
  const selected = !!institutionId && !usesDefault;

  useEffect(() => {
    if (disabled || selected || manual || (usesDefault && !choosing)) return;
    let cancelled = false;
    // Petite attente : une requête par pause de frappe, pas par lettre.
    const timer = setTimeout(() => {
      setLoading(true);
      setFailed(false);
      financialInstitutionApiService
        .listSupportPrograms({ search, province })
        .then((list) => !cancelled && setOptions(list))
        .catch(() => !cancelled && setFailed(true))
        .finally(() => !cancelled && setLoading(false));
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [search, province, disabled, selected, manual, usesDefault, choosing]);

  useEffect(() => {
    if (!institutionId || logo?.id === institutionId) return;
    let cancelled = false;
    financialInstitutionApiService
      .listSupportPrograms()
      .then((list) => {
        if (cancelled) return;
        const found = list.find((o) => o.id === institutionId);
        setLogo({ id: institutionId, url: found?.logoUrl || null });
      })
      .catch(() => !cancelled && setLogo({ id: institutionId, url: null }));
    return () => {
      cancelled = true;
    };
  }, [institutionId, logo?.id]);

  const label = (
    <label className="block text-sm font-medium text-gray-700 mb-1">Programme d'appui</label>
  );
  // Pourquoi ce choix compte : le programme figure sur les pièces du groupe.
  const hint = (
    <p className="mt-1 text-xs text-gray-500">
      Facultatif. Le programme qui suit votre AVEC figure sur les PV, carnets et rapports du groupe
      (« Appuyée par ... »). Sans choix, c'est {DEFAULT_SUPPORT_PROGRAM.name}, et vous pouvez en changer à tout moment.
    </p>
  );

  // Structure d'appui par défaut : affichée telle quelle, modifiable.
  if (usesDefault && !manual && (!choosing || disabled)) {
    return (
      <div>
        {label}
        <div className="flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
          <span className="min-w-0 truncate">
            {DEFAULT_SUPPORT_PROGRAM.name} <span className="text-gray-500">(structure d'appui par défaut)</span>
          </span>
          {!disabled && (
            <button type="button" className="text-[#197ca8] hover:underline flex-shrink-0" onClick={() => setChoosing(true)}>
              Choisir un autre programme
            </button>
          )}
        </div>
        {!disabled && hint}
      </div>
    );
  }
  const logoUrl = selected && logo?.id === institutionId ? logo.url : null;

  if (selected || (disabled && name)) {
    return (
      <div>
        {label}
        <div className="flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm">
          <span className="flex min-w-0 items-center gap-2">
            {logoUrl && (
              <img src={logoUrl} alt="" className="h-6 w-6 flex-shrink-0 rounded bg-white object-contain" />
            )}
            <span className="truncate">{name || 'Programme choisi'}</span>
          </span>
          {!disabled && (
            <button
              type="button"
              className="text-[#197ca8] hover:underline flex-shrink-0"
              onClick={() => {
                setChoosing(true);
                onChange({ supportProgramInstitutionId: '', supportProgramName: '' });
              }}
            >
              Changer
            </button>
          )}
        </div>
        {!disabled && hint}
      </div>
    );
  }

  if (manual) {
    return (
      <div>
        {label}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={name}
            disabled={disabled}
            placeholder="Nom du programme ou de l'ONG"
            onChange={(e) => onChange({ supportProgramInstitutionId: '', supportProgramName: e.target.value })}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-[#197ca8] focus:ring-[#197ca8]"
          />
          {!disabled && (
            <button
              type="button"
              title="Revenir à la liste"
              className="text-gray-400 hover:text-gray-600"
              onClick={() => {
                setManual(false);
                onChange({ supportProgramInstitutionId: '', supportProgramName: '' });
              }}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Votre programme n'est pas encore sur Wanzo : son nom seul figurera sur les pièces du groupe.
        </p>
        {hint}
      </div>
    );
  }

  return (
    <div>
      {label}
      <div className="relative">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={search}
          disabled={disabled}
          placeholder={province ? `Chercher un programme (${province})` : 'Chercher un programme'}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm focus:border-[#197ca8] focus:ring-[#197ca8]"
        />
      </div>
      {!disabled && (
        <div className="mt-1 max-h-48 overflow-y-auto rounded-md border border-gray-200 bg-white text-sm">
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Recherche...
            </div>
          )}
          {!loading && failed && (
            <p className="px-3 py-2 text-gray-500">La liste des programmes est indisponible pour le moment.</p>
          )}
          {!loading && !failed && options.length === 0 && (
            <p className="px-3 py-2 text-gray-500">Aucun programme trouvé.</p>
          )}
          {!loading &&
            options.map((o) => (
              <button
                key={o.id}
                type="button"
                className="block w-full px-3 py-2 text-left hover:bg-gray-50"
                onClick={() => {
                  setLogo({ id: o.id, url: o.logoUrl || null });
                  onChange({ supportProgramInstitutionId: o.id, supportProgramName: o.name });
                }}
              >
                <span className="flex items-start gap-2">
                  {o.logoUrl && (
                    <img src={o.logoUrl} alt="" className="mt-0.5 h-6 w-6 flex-shrink-0 rounded object-contain" />
                  )}
                  <span className="min-w-0">
                    <span className="font-medium">{o.name}</span>
                    {o.sigle && <span className="text-gray-500"> ({o.sigle})</span>}
                    {o.provinces.length > 0 && (
                      <span className="block text-xs text-gray-500">{o.provinces.join(', ')}</span>
                    )}
                  </span>
                </span>
              </button>
            ))}
          <button
            type="button"
            className="block w-full border-t border-gray-100 px-3 py-2 text-left text-[#197ca8] hover:bg-gray-50"
            onClick={() => setManual(true)}
          >
            Mon programme n'est pas dans la liste
          </button>
          <button
            type="button"
            className="block w-full border-t border-gray-100 px-3 py-2 text-left text-gray-600 hover:bg-gray-50"
            onClick={() => {
              setChoosing(false);
              setSearch('');
              onChange({ supportProgramInstitutionId: '', supportProgramName: '' });
            }}
          >
            Garder {DEFAULT_SUPPORT_PROGRAM.name} (par défaut)
          </button>
        </div>
      )}
      {hint}
    </div>
  );
};

export default SupportProgramPicker;
